// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react';

import type { CeilingPlane, ModelEditOp } from '../../api/types';
import type { GeometriEditor } from '../../app/bygning/GeometriEditorContext';
import { useCanEdit } from '../../app/CaseRoleContext';
import { shiftCeilingOps } from '../../bygning/ceilingEdit';
import { findElement } from '../../bygning/geometri';
import { editLabel, fieldOps } from '../../bygning/geometriEdit';
import { clickSelect } from '../../bygning/geometriSelect';
import {
  ceilingAt,
  fitSection,
  sectionPoint,
  heightHistogram,
  histogramPeaks,
  sectionCut,
  sectionLength,
  sliceCloud,
  snapHeight,
  type SectionCut,
  type SectionView,
} from '../../bygning/section';
import { levelText, storeyHeightOps } from '../../bygning/storeyEdit';
import { CadIcon } from '../../components/bygning/rediger/cadIcons';
import { Spinner } from '../../components/Spinner';
import { useSectionCloud } from './useSectionCloud';
import styles from './SnitPanel.module.css';

/** Room round the content when the view fits, px. */
const PAD = 28;
/** The label gutter on the left (storey names and levels), px. */
const GUTTER = 112;
/** The histogram's widest bar, px. */
const HIST_W = 64;
/** A height snaps to a peak or a level this close, px. */
const SNAP_PX = 8;
/** A press that moves less than this is a click. */
const DRAG_PX = 3;
/** A level tag's height in the gutter, px. */
const TAG_H = 18;

export interface SnitPanelProps {
  editor: GeometriEditor;
  /** The storey the plan shows (null: every storey): its band is the one in focus. */
  storeyId: number | null;
  /** 'snit': the section alone; 'split': under the plan. */
  layout: 'snit' | 'split';
  /** Egenskaber or Etager is open on the right: the panel stops short of it. */
  besideRight: boolean;
  /** Tilpas til punktsky waiting for Anvend: the fitted planes of room `guid`, drawn over its ceiling. */
  preview?: { guid: string; planes: CeilingPlane[] } | null;
}

/** What a grip drag moves. */
type GripTarget =
  | { kind: 'floor' | 'ceiling'; guid: string; name: string; z0: number }
  | { kind: 'room'; guid: string; name: string; z0: number }
  | { kind: 'sill' | 'head'; guid: string; name: string; z0: number; other: number }
  | { kind: 'wallTop'; guid: string; name: string; z0: number; base: number }
  | { kind: 'slabTop'; guid: string; name: string; z0: number };

interface Drag {
  target: GripTarget;
  z: number;
  snapped: boolean;
  moved: boolean;
}

const DEPTHS: { value: number; label: string }[] = [
  { value: 0.1, label: '0,10 m' },
  { value: 0.3, label: '0,30 m' },
  { value: 0.6, label: '0,60 m' },
  { value: 1.5, label: '1,50 m' },
];

const signed = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2).replace('.', ',')}`;
const GRIP_NAME: Record<GripTarget['kind'], string> = {
  floor: 'Gulv',
  ceiling: 'Loft',
  room: 'Loft',
  sill: 'Brystning',
  head: 'Overkant',
  wallTop: 'Væggens top',
  slabTop: 'Dækkets overkant',
};

/** Per element, its widest stretch of the cut: one grip each, however many holes split it. */
function widest<T extends { guid: string; s0: number; s1: number }>(items: readonly T[]): T[] {
  const best = new Map<string, T>();
  for (const it of items) {
    const b = best.get(it.guid);
    if (!b || it.s1 - it.s0 > b.s1 - b.s0) best.set(it.guid, it);
  }
  return [...best.values()];
}

/** A token's value, for the canvas (which cannot take `var()`). */
function tokenColour(el: Element | null, name: string): string {
  if (!el) return 'currentColor';
  return getComputedStyle(el).getPropertyValue(name).trim() || 'currentColor';
}

/**
 * Snit (frontend-u0o.7, slice S7): a vertical section along the cut A–A,
 * alone or under the plan. The scan's points in the cut's band are the
 * reference; over them the storey bands (their floors and ceilings are
 * grips: drag one to set the height, snapping to the floors and ceilings
 * the cloud's height histogram finds, drawn on the right), the walls cut
 * to their thickness with the openings the cut passes through, the slabs,
 * and each room's ceiling (drag it to raise or lower the whole ceiling). A
 * selected opening shows its sill and head grips, a selected wall its top,
 * a selected slab its upper face. A click selects; the wheel zooms, a drag
 * on empty space pans, a double click fits.
 */
export function SnitPanel({ editor, storeyId, layout, besideRight, preview = null }: SnitPanelProps) {
  const canEdit = useCanEdit();
  const line = editor.section;
  const model = editor.model;
  const bodyRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [view, setView] = useState<SectionView | null>(null);
  const [showHist, setShowHist] = useState(true);
  const [drag, setDragState] = useState<Drag | null>(null);
  const [cursor, setCursor] = useState<[number, number] | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const setDrag = (d: Drag | null) => {
    dragRef.current = d;
    setDragState(d);
  };

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  const { cloud, loading } = useSectionCloud(true);
  const cut: SectionCut | null = useMemo(() => (line ? sectionCut(model, line) : null), [model, line]);
  const slice = useMemo(() => (line && cloud ? sliceCloud(cloud.xyz, line) : null), [cloud, line]);
  const hist = useMemo(() => {
    if (!cloud) return null;
    const zs = new Float32Array(cloud.xyz.length / 3);
    for (let i = 0; i < zs.length; i += 1) zs[i] = cloud.xyz[i * 3 + 2];
    return heightHistogram(zs, { bin: 0.05 });
  }, [cloud]);
  const peaks = useMemo(() => (hist ? histogramPeaks(hist) : []), [hist]);

  // ------------------------------------------------------------ view --
  const L = line ? sectionLength(line) : 0;
  const zRange = useMemo<[number, number]>(() => {
    const st = cut?.storeys ?? [];
    const focus = st.find((s) => s.id === storeyId);
    if (focus) return [focus.z0 - 0.6, focus.z1 + 0.6];
    if (st.length > 0) return [Math.min(...st.map((s) => s.z0)) - 0.6, Math.max(...st.map((s) => s.z1)) + 0.6];
    const zs = [...(cut?.walls ?? []).flatMap((w) => [w.z0, w.z1]), ...(cut?.slabs ?? []).flatMap((s) => [s.z0, s.z1])];
    return zs.length > 0 ? [Math.min(...zs) - 0.5, Math.max(...zs) + 0.5] : [-0.5, 3];
  }, [cut, storeyId]);
  const plotW = Math.max(10, size.w - GUTTER - (showHist ? HIST_W + 12 : 0));
  const auto = useMemo(() => {
    const v = fitSection([0, Math.max(L, 1)], zRange, plotW, size.h, PAD);
    return { ...v, ox: v.ox + GUTTER };
  }, [L, zRange, plotW, size.h]);
  const fitKey = `${line?.a.join(',')}|${line?.b.join(',')}|${storeyId}|${layout}`;
  useEffect(() => setView(null), [fitKey]);
  const v = view ?? auto;
  const sx = (s: number) => v.ox + s * v.k;
  const sy = (z: number) => v.oy - z * v.k;
  const zAt = (py: number) => (v.oy - py) / v.k;
  const sAt = (px: number) => (px - v.ox) / v.k;

  // ------------------------------------------------------------ points --
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || size.w === 0) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(size.w * dpr);
    c.height = Math.round(size.h * dpr);
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);
    if (!slice) return;
    ctx.fillStyle = tokenColour(c, '--color-text-muted');
    ctx.globalAlpha = 0.55;
    const r = v.k > 120 ? 1.6 : 1.2;
    for (let i = 0; i + 1 < slice.length; i += 2) {
      const x = v.ox + slice[i] * v.k;
      const y = v.oy - slice[i + 1] * v.k;
      if (x < GUTTER - 4 || x > size.w || y < -2 || y > size.h + 2) continue;
      ctx.fillRect(x - r / 2, y - r / 2, r, r);
    }
  }, [slice, v.k, v.ox, v.oy, size.w, size.h]);

  // ------------------------------------------------------------ grips --
  const snapTargets = useCallback(
    (target: GripTarget): number[] => {
      const levels = (cut?.storeys ?? []).flatMap((s) => (s.guid === target.guid ? [] : [s.z0, s.z1]));
      return [...peaks, ...levels];
    },
    [cut, peaks],
  );

  const commitDrag = useCallback(
    (d: Drag) => {
      const ed = editor;
      const t = d.target;
      let res: { ops: ModelEditOp[]; label: string } | { error: string } | null = null;
      if (t.kind === 'floor' || t.kind === 'ceiling') res = storeyHeightOps(ed.model, t.guid, t.kind === 'floor' ? { zMin: d.z } : { zMax: d.z }, ed.storeyCarry);
      else if (t.kind === 'room') res = shiftCeilingOps(ed.model, t.guid, d.z - t.z0);
      else if (t.kind === 'sill' || t.kind === 'head') {
        const z = Math.round(d.z * 1000) / 1000;
        if (t.kind === 'sill' ? z > t.other - 0.1 : z < t.other + 0.1) res = { error: 'Åbningen skal være mindst 10 cm høj' };
        else res = { ops: [{ op: 'opening.update', guid: t.guid, set: t.kind === 'sill' ? { z_sill: z } : { z_head: z } }], label: editLabel(GRIP_NAME[t.kind], ed.model, [t.guid]) };
      } else if (t.kind === 'wallTop') {
        const ops = fieldOps(ed.model, t.guid, 'height', d.z - t.base);
        res = ops ? { ops, label: editLabel('Højde', ed.model, [t.guid]) } : null;
      } else if (t.kind === 'slabTop') {
        const f = findElement(ed.model, t.guid);
        const floor = f && f.kind === 'slab' ? (ed.model.storeys?.find((s) => s.id === f.row.storey_id)?.z_min ?? 0) : 0;
        const ops = fieldOps(ed.model, t.guid, 'slab_top', d.z - floor);
        res = ops ? { ops, label: editLabel('Overkant', ed.model, [t.guid]) } : null;
      }
      if (!res) return;
      if ('error' in res) ed.notify(res.error);
      else void ed.commit(res.ops, res.label);
    },
    [editor],
  );

  const startGrip = (target: GripTarget, e: ReactPointerEvent) => {
    if (e.button !== 0 || !canEdit) return;
    e.stopPropagation();
    e.preventDefault();
    const y0 = e.clientY;
    const rect = bodyRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDrag({ target, z: target.z0, snapped: false, moved: false });
    const move = (m: PointerEvent) => {
      const cur = dragRef.current;
      if (!cur) return;
      if (!cur.moved && Math.abs(m.clientY - y0) < DRAG_PX) return;
      const raw = zAt(m.clientY - rect.top);
      const snapped = m.altKey ? null : snapHeight(raw, snapTargets(target), SNAP_PX / v.k);
      setDrag({ ...cur, moved: true, z: snapped ?? Math.round(raw * 100) / 100, snapped: snapped !== null });
    };
    const end = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('keydown', esc, true);
    };
    const up = () => {
      end();
      const cur = dragRef.current;
      setDrag(null);
      if (cur?.moved && Math.abs(cur.z - cur.target.z0) > 5e-4) commitDrag(cur);
    };
    const esc = (k: KeyboardEvent) => {
      if (k.key !== 'Escape') return;
      k.preventDefault();
      k.stopPropagation();
      end();
      setDrag(null);
    };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('keydown', esc, true);
  };

  // ---------------------------------------------------- pan, zoom, pick --
  const onWheel = (e: ReactWheelEvent) => {
    const rect = bodyRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const f = Math.exp(-e.deltaY * 0.0015);
    const k = Math.min(4000, Math.max(4, v.k * f));
    const s = sAt(px);
    const z = zAt(py);
    setView({ k, ox: px - s * k, oy: py + z * k });
  };
  const onBackgroundDown = (e: ReactPointerEvent) => {
    if (e.button !== 0 && e.button !== 1 && e.button !== 2) return;
    const x0 = e.clientX;
    const y0 = e.clientY;
    const start = v;
    let moved = false;
    const move = (m: PointerEvent) => {
      if (!moved && Math.hypot(m.clientX - x0, m.clientY - y0) < DRAG_PX) return;
      moved = true;
      setView({ k: start.k, ox: start.ox + (m.clientX - x0), oy: start.oy + (m.clientY - y0) });
    };
    const up = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      // A click on empty section clears the selection, as on the plan.
      if (!moved && e.button === 0 && !e.shiftKey && editor.tool === 'select') editor.select([]);
    };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
  };
  const pick = (guid: string) => (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    editor.select(clickSelect(editor.selection, guid, e.shiftKey || e.metaKey || e.ctrlKey));
  };
  const onMove = (e: ReactPointerEvent) => {
    const rect = bodyRef.current?.getBoundingClientRect();
    if (rect) setCursor([sAt(e.clientX - rect.left), zAt(e.clientY - rect.top)]);
  };

  // --------------------------------------------------------------- draw --
  const sel = new Set(editor.selection);
  const shownZ = (t: GripTarget) => (drag && drag.target.guid === t.guid && drag.target.kind === t.kind ? drag.z : t.z0);
  const x0 = GUTTER;
  const x1 = size.w - (showHist ? HIST_W + 12 : 0);
  const storeyGrips: GripTarget[] = (cut?.storeys ?? []).flatMap((s) => [
    { kind: 'floor' as const, guid: s.guid, name: s.name, z0: s.z0 },
    { kind: 'ceiling' as const, guid: s.guid, name: s.name, z0: s.z1 },
  ]);
  // The gutter's tags: a floor's on its line inside its band, a ceiling's under its line; pushed apart where they would overlap.
  const tags = (() => {
    const want = storeyGrips.map((t) => {
      const y = sy(shownZ(t));
      return { t, y, top: t.kind === 'floor' ? y - TAG_H - 1 : y + 1 };
    });
    want.sort((a, b) => a.top - b.top);
    let floor = -Infinity;
    for (const w of want) {
      w.top = Math.max(w.top, floor + 1);
      floor = w.top + TAG_H;
    }
    return want;
  })();
  const histBars = useMemo(() => {
    if (!hist || !showHist) return null;
    const max = Math.max(1, ...hist.counts);
    return hist.counts.map((c, i) => ({ z0: hist.z0 + i * hist.bin, z1: hist.z0 + (i + 1) * hist.bin, w: (Math.sqrt(c / max) * HIST_W) | 0 }));
  }, [hist, showHist]);

  const readout = drag?.moved
    ? `${GRIP_NAME[drag.target.kind]} · ${drag.target.name} ${levelText(drag.z)} · Δ ${signed(drag.z - drag.target.z0)} m${drag.snapped ? ' · fanget' : ''}`
    : cursor
      ? `${cursor[0].toFixed(2).replace('.', ',')} m langs · kote ${levelText(cursor[1])}`
      : '';

  if (!line) {
    return (
      <section className={`${styles.panel} ${layout === 'split' ? styles.split : styles.full}${besideRight ? ` ${styles.besideRight}` : ''}`} aria-label="Snit">
        <SnitHead editor={editor} length={null} showHist={showHist} onHist={() => setShowHist((h) => !h)} onFit={() => setView(null)} layout={layout} />
        <div ref={bodyRef} className={styles.empty}>
          <p>Intet snit endnu. Tegn en snitlinje på planen, eller vælg et element og tryk SN.</p>
        </div>
      </section>
    );
  }

  const stroke = (guid: string) => (sel.has(guid) ? styles.selected : '');
  return (
    <section className={`${styles.panel} ${layout === 'split' ? styles.split : styles.full}${besideRight ? ` ${styles.besideRight}` : ''}`} aria-label="Snit A–A">
      <SnitHead editor={editor} length={L} showHist={showHist} onHist={() => setShowHist((h) => !h)} onFit={() => setView(null)} layout={layout} />
      <div ref={bodyRef} className={styles.body} onWheel={onWheel} onPointerMove={onMove} onPointerLeave={() => setCursor(null)} onContextMenu={(e) => e.preventDefault()}>
        <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />
        <svg className={styles.svg} onPointerDown={onBackgroundDown} onDoubleClick={() => setView(null)}>
          <defs>
            <pattern id="snit-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="6" className={styles.hatchLine} />
            </pattern>
          </defs>
          {/* Storey bands: the focused one in the accent, the rest alternating. */}
          {(cut?.storeys ?? []).map((s, i) => (
            <rect key={`b${s.guid}`} x={x0} y={sy(s.z1)} width={Math.max(0, x1 - x0)} height={Math.max(0, sy(s.z0) - sy(s.z1))} className={s.id === storeyId ? styles.bandOn : i % 2 ? styles.bandAlt : styles.band} />
          ))}
          {/* Rooms: a faint fill under their ceiling, the ceiling line draggable. */}
          {cut?.spaces.map((sp, i) => {
            const pts = sp.ceiling.map(([s, z]) => `${sx(s).toFixed(1)},${sy(z).toFixed(1)}`);
            const dz = drag?.target.kind === 'room' && drag.target.guid === sp.guid ? drag.z - drag.target.z0 : 0;
            const ceil = sp.ceiling.map(([s, z]) => `${sx(s).toFixed(1)},${sy(z + dz).toFixed(1)}`).join(' ');
            const fill = `${sx(sp.s0)},${sy(sp.floor)} ${pts.join(' ')} ${sx(sp.s1)},${sy(sp.floor)}`;
            const f = findElement(model, sp.guid);
            const name = f?.kind === 'space' ? f.row.name || 'Rum' : 'Rum';
            const mid = sp.ceiling[Math.floor(sp.ceiling.length / 2)];
            const target: GripTarget = { kind: 'room', guid: sp.guid, name, z0: mid[1] };
            return (
              <g key={`r${sp.guid}${i}`}>
                <polygon points={fill} className={`${styles.room} ${stroke(sp.guid)}`} onPointerDown={pick(sp.guid)} />
                <polyline points={ceil} className={sel.has(sp.guid) ? styles.ceilingOn : styles.ceiling} />
                {canEdit && <polyline points={ceil} className={styles.hit} onPointerDown={(e) => startGrip(target, e)}><title>{`Træk loftet i ${name}`}</title></polyline>}
                {canEdit && sel.has(sp.guid) && (
                  <rect x={sx((sp.s0 + sp.s1) / 2) - 7} y={sy(mid[1] + dz) - 3} width={14} height={6} rx={2} className={styles.grip} onPointerDown={(e) => startGrip(target, e)}>
                    <title>Træk loftet op eller ned</title>
                  </rect>
                )}
              </g>
            );
          })}
          {/* Tilpas til punktsky: the fitted ceiling, before Anvend. */}
          {preview && line &&
            cut?.spaces
              .filter((sp) => sp.guid === preview.guid)
              .map((sp, i) => {
                const pts: string[] = [];
                const n = Math.max(2, Math.ceil((sp.s1 - sp.s0) / 0.1));
                for (let k = 0; k <= n; k += 1) {
                  const s = sp.s0 + ((sp.s1 - sp.s0) * k) / n;
                  const p = sectionPoint(line, s);
                  pts.push(`${sx(s).toFixed(1)},${sy(ceilingAt(preview.planes, Infinity, p[0], p[1])).toFixed(1)}`);
                }
                return <polyline key={`pv${i}`} points={pts.join(' ')} className={styles.fitLine} />;
              })}
          {/* Slabs, hatched. */}
          {cut?.slabs.map((sl, i) => (
            <rect
              key={`s${sl.guid}${i}`}
              x={sx(sl.s0)}
              y={sy(sl.z1)}
              width={Math.max(1, sx(sl.s1) - sx(sl.s0))}
              height={Math.max(1, sy(sl.z0) - sy(sl.z1))}
              className={`${styles.slab} ${stroke(sl.guid)}`}
              onPointerDown={pick(sl.guid)}
            />
          ))}
          {/* Walls cut to their thickness, poché. */}
          {cut?.walls.map((w, i) => (
            <rect key={`w${w.guid}${i}`} x={sx(w.s0)} y={sy(w.z1)} width={Math.max(1.5, sx(w.s1) - sx(w.s0))} height={Math.max(1, sy(w.z0) - sy(w.z1))} className={`${styles.wall} ${stroke(w.guid)}`} onPointerDown={pick(w.guid)} />
          ))}
          {/* Openings the cut passes through: the gap in the wall, sill and head. */}
          {cut?.openings.map((o, i) => {
            const sill = drag?.target.kind === 'sill' && drag.target.guid === o.guid ? drag.z : o.sill;
            const head = drag?.target.kind === 'head' && drag.target.guid === o.guid ? drag.z : o.head;
            const w = Math.max(6, sx(o.s1) - sx(o.s0));
            const xm = sx(o.s0) - (w - (sx(o.s1) - sx(o.s0))) / 2;
            return (
              <g key={`o${o.guid}${i}`}>
                <rect x={xm} y={sy(head)} width={w} height={Math.max(1, sy(sill) - sy(head))} className={`${styles.opening} ${stroke(o.guid)}`} onPointerDown={pick(o.guid)} />
                {canEdit && sel.has(o.guid) && (
                  <>
                    <rect x={xm + w / 2 - 7} y={sy(head) - 3} width={14} height={6} rx={2} className={styles.grip} onPointerDown={(e) => startGrip({ kind: 'head', guid: o.guid, name: 'Åbning', z0: o.head, other: o.sill }, e)}>
                      <title>Træk overkanten</title>
                    </rect>
                    <rect x={xm + w / 2 - 7} y={sy(sill) - 3} width={14} height={6} rx={2} className={styles.grip} onPointerDown={(e) => startGrip({ kind: 'sill', guid: o.guid, name: 'Åbning', z0: o.sill, other: o.head }, e)}>
                      <title>Træk brystningen</title>
                    </rect>
                  </>
                )}
              </g>
            );
          })}
          {/* A selected wall's top, a selected slab's upper face. */}
          {canEdit &&
            widest(cut?.walls.filter((w) => sel.has(w.guid)) ?? [])
              .map((w, i) => {
                const t: GripTarget = { kind: 'wallTop', guid: w.guid, name: 'Væg', z0: w.z1, base: w.z0 };
                return (
                  <rect key={`wt${w.guid}${i}`} x={(sx(w.s0) + sx(w.s1)) / 2 - 7} y={sy(shownZ(t)) - 3} width={14} height={6} rx={2} className={styles.grip} onPointerDown={(e) => startGrip(t, e)}>
                    <title>Træk væggens top</title>
                  </rect>
                );
              })}
          {canEdit &&
            widest(cut?.slabs.filter((s) => sel.has(s.guid)) ?? [])
              .map((s, i) => {
                const t: GripTarget = { kind: 'slabTop', guid: s.guid, name: 'Dæk', z0: s.z1 };
                return (
                  <rect key={`st${s.guid}${i}`} x={(sx(s.s0) + sx(s.s1)) / 2 - 7} y={sy(shownZ(t)) - 3} width={14} height={6} rx={2} className={styles.grip} onPointerDown={(e) => startGrip(t, e)}>
                    <title>Træk dækkets overkant</title>
                  </rect>
                );
              })}
          {/* The storeys' floors and ceilings: lines across, grips in the gutter. */}
          {storeyGrips.map((t) => {
            const y = sy(shownZ(t));
            const on = drag?.target.guid === t.guid && drag.target.kind === t.kind;
            return (
              <g key={`${t.kind}${t.guid}`}>
                <line x1={x0} x2={x1} y1={y} y2={y} className={on ? styles.levelOn : t.kind === 'floor' ? styles.level : styles.levelCeiling} />
                {canEdit && <line x1={x0} x2={x1} y1={y} y2={y} className={styles.hitLevel} onPointerDown={(e) => startGrip(t, e)}><title>{`Træk ${t.kind === 'floor' ? 'gulvet' : 'loftet'} på ${t.name}`}</title></line>}
              </g>
            );
          })}
          {tags.map(({ t, y, top }) => {
            const on = drag?.target.guid === t.guid && drag.target.kind === t.kind;
            const focus = (cut?.storeys ?? []).find((s) => s.guid === t.guid)?.id === storeyId;
            const mid = top + TAG_H / 2;
            return (
              <g key={`tag${t.kind}${t.guid}`} className={focus || on ? styles.tagOn : styles.tag} onPointerDown={(e) => startGrip(t, e)}>
                {Math.abs(mid - y) > TAG_H / 2 + 1 && <line x1={GUTTER - 6} x2={x0} y1={mid} y2={y} className={styles.leader} />}
                <rect x={4} y={top} width={GUTTER - 10} height={TAG_H} rx={4} />
                <text x={10} y={top + 13}>
                  {t.kind === 'floor' ? 'Gulv' : 'Loft'} {levelText(shownZ(t))}
                </text>
              </g>
            );
          })}
          {(cut?.storeys ?? []).map((s) =>
            sy(s.z0) - sy(s.z1) < TAG_H * 3 ? null : (
              <text key={`n${s.guid}`} x={10} y={(sy(s.z0) + sy(s.z1)) / 2 + 4} className={s.id === storeyId ? styles.storeyNameOn : styles.storeyName}>
                {s.isGround ? `${s.name} · S` : s.name}
              </text>
            ),
          )}
          {/* The cloud's height histogram: its peaks are the floors and ceilings it saw. */}
          {histBars && (
            <g className={styles.hist}>
              <line x1={size.w - 6} x2={size.w - 6} y1={0} y2={size.h} className={styles.histAxis} />
              {histBars.map((b, i) => {
                const yTop = sy(b.z1);
                const yBot = sy(b.z0);
                if (b.w === 0 || yBot < 0 || yTop > size.h) return null;
                return <rect key={i} x={size.w - 6 - b.w} y={yTop} width={b.w} height={Math.max(0.5, yBot - yTop - 0.3)} className={styles.histBar} />;
              })}
              {peaks.map((z) => (
                <g key={z}>
                  <line x1={size.w - HIST_W - 10} x2={size.w - 6} y1={sy(z)} y2={sy(z)} className={styles.peak} />
                  <text x={size.w - HIST_W - 12} y={sy(z) + 3} className={styles.peakText}>
                    {levelText(z)}
                  </text>
                </g>
              ))}
            </g>
          )}
          {/* A and A at the ends. */}
          <text x={sx(0)} y={14} className={styles.endMark}>
            A
          </text>
          <text x={sx(L)} y={14} className={styles.endMark}>
            A′
          </text>
        </svg>
        {loading && (
          <div className={styles.loading}>
            <Spinner label="Henter punktskyen…" />
          </div>
        )}
        <p className={styles.readout} aria-live="off">
          {readout}
        </p>
      </div>
    </section>
  );
}

function SnitHead({
  editor,
  length,
  showHist,
  onHist,
  onFit,
  layout,
}: {
  editor: GeometriEditor;
  length: number | null;
  showHist: boolean;
  onHist: () => void;
  onFit: () => void;
  layout: 'snit' | 'split';
}) {
  const line = editor.section;
  return (
    <header className={styles.head}>
      <h2 className={styles.title}>
        Snit A–A{length !== null && <small>{length.toFixed(2).replace('.', ',')} m</small>}
      </h2>
      <div className={styles.actions}>
        <button type="button" className={styles.action} aria-pressed={editor.sectionDraw} onClick={() => editor.run(editor.sectionDraw ? 'escape' : 'section-draw')} title="Klik to punkter på planen">
          {editor.sectionDraw ? 'Klik A og B på planen…' : 'Ny snitlinje'}
        </button>
        <button type="button" className={styles.action} disabled={editor.selection.length === 0} onClick={() => editor.run('section')} title="Snit gennem det valgte (SN)">
          Gennem valgte
        </button>
        {line && (
          <label className={styles.depth} title="Hvor bredt et bånd af punktskyen snittet viser">
            Dybde
            <select
              value={String(line.depth)}
              onChange={(e) => editor.setSection({ ...line, depth: Number(e.target.value) })}
              aria-label="Snittets dybde"
            >
              {DEPTHS.map((d) => (
                <option key={d.value} value={String(d.value)}>
                  {d.label}
                </option>
              ))}
              {!DEPTHS.some((d) => d.value === line.depth) && <option value={String(line.depth)}>{line.depth.toFixed(2).replace('.', ',')} m</option>}
            </select>
          </label>
        )}
        {line && (
          <button type="button" className={styles.action} onClick={() => editor.setSection({ ...line, a: line.b, b: line.a })} title="Se snittet fra den anden side">
            Vend
          </button>
        )}
        <button type="button" className={styles.action} aria-pressed={showHist} onClick={onHist} title="Punktskyens højdefordeling: toppene er gulve og lofter">
          Højder
        </button>
        <button type="button" className={styles.iconButton} onClick={onFit} title="Tilpas (dobbeltklik)" aria-label="Tilpas snittet">
          <CadIcon name="fit" className={styles.icon} />
        </button>
        <button
          type="button"
          className={styles.action}
          onClick={() => editor.run(layout === 'snit' ? 'view:split' : 'view:snit')}
          title={layout === 'snit' ? 'Vis planen over snittet' : 'Vis snittet alene'}
        >
          {layout === 'snit' ? 'Plan + snit' : 'Kun snit'}
        </button>
        <button type="button" className={styles.iconButton} onClick={() => editor.run('view:plan')} title="Luk snittet" aria-label="Luk snittet">
          ×
        </button>
      </div>
    </header>
  );
}
